import { Manifest, ManifestSeries, ManifestEpisode, IndexDto, EntityCollection, Entity, seriesKey } from 'src/domain/data';

export interface SearchRequest {
	searchName?: string;
	searchRelated?: string;
	tags?: Set<string>;
	series?: Set<string>;
	episodes?: Set<string>;
	excludeKeys?: Set<string>;
}

export class DataProvider {
	private manifestPath: string;
	public readonly data = {
		entries: new EntityCollection(),
		seriesWithEpisodes: new Map<string, { series: ManifestSeries, episodes: Set<ManifestEpisode> }>(),
		tags: <string[]>[],
	};

	constructor(dataPath: string) {
		this.manifestPath = dataPath;
	}

	public async loadData(): Promise<void> {
		try {
			const manifestResponse = await fetch(this.manifestPath);
			if (!manifestResponse.ok) {
				throw new Error('Could not load episode manifest');
			}

			const manifest: Manifest = await manifestResponse.json();
			for (const seriesEntry of manifest.series) {
				const episodes = new Set<ManifestEpisode>();
				this.data.seriesWithEpisodes.set(seriesEntry.key, { series: seriesEntry, episodes: episodes });
				for (const episodeEntry of seriesEntry.episodes) {
					try {
						const indexResponse = await fetch(`data/${seriesEntry.key}/index.json`);
						if (!indexResponse.ok) continue;

						episodes.add(episodeEntry);
						const index: IndexDto = await indexResponse.json();
						this.data.entries.setSeries(seriesEntry.key, index.entities);
					} catch (err) {
					}
				}
			}

			this.data.entries.sort();
			this.data.tags.push(...this.data.entries.getAllTags());

			if (this.data.entries.length === 0) {
				throw new Error('No episodes loaded');
			}
		} catch (error) {
			console.error(`Failed to load episodes. Make sure you have a data/manifest.json file listing your episode files. Error: ${error}`);
		}
	}

	public searchEntries(request: SearchRequest): Entity[] {
		const searcher = new DataSearch(request);
		return searcher.search(this.data.entries);
	}
}

class DataSearch {
	constructor(private searchRequest: SearchRequest) { }

	search(entries: EntityCollection): Entity[] {
		const results: Entity[] = [];
		entries.forEach(entry => {
			if (this.matchExcludeKeys(entry)) return;
			const filteredAliases = this.filterEntityData(entry.alias);
			if (!this.matchSearchName(entry, filteredAliases)) return;
			const filteredEntry: Entity = {
				key: entry.key,
				links: entry.links,
				depiction: this.filterEntityData(entry.depiction),
				alias: filteredAliases,
				connections: this.filterEntityData(entry.connections),
				tags: this.filterEntityData(entry.tags)
			};
			if (filteredEntry.depiction.size === 0 && filteredEntry.tags.size === 0 && filteredEntry.connections.size === 0) return;
			if (!this.matchTags(filteredEntry)) return;
			if (!this.matchSearchRelated(filteredEntry.depiction, filteredEntry.tags, filteredEntry.connections)) return;
			results.push(filteredEntry);
		});

		return results;
	}

	private matchExcludeKeys(item: Entity): boolean {
		if (!this.searchRequest.excludeKeys || this.searchRequest.excludeKeys.size === 0) return false;
		return this.searchRequest.excludeKeys.has(item.key);
	}

	private matchSearchName(item: Entity, ...entityData: Map<string, string | string[]>[]): boolean {
		if (!this.searchRequest.searchName) return true;
		const lowerSearchTerm = this.searchRequest.searchName.toLowerCase();
		return item.key.toLowerCase().includes(lowerSearchTerm) || entityData.some(data => this.searchEntityData(data, lowerSearchTerm));
	}

	private filterEntityData<T extends string | string[]>(entityData: Map<string, T>): Map<string, T> {
		return new Map([...entityData].filter(([entityKey, _]) =>
			this.matchSeries(entityKey)
			&& this.matchEpisodes(entityKey)));
	}

	private matchSearchRelated(...entityData: Map<string, string | string[]>[]): boolean {
		if (!this.searchRequest.searchRelated) return true;
		const lowerSearchTerm = this.searchRequest.searchRelated.toLowerCase();
		return entityData.some(data => this.searchEntityData(<any>data, lowerSearchTerm));
	}

	private searchEntityData<T extends string | string[]>(entityData: Map<string, T>, searchTerm: string): boolean {
		if (!searchTerm) return true;
		return [...entityData].some(([_, data]) =>
			((typeof data === "string" ? (<string>data).toLowerCase().includes(searchTerm) : (<string[]>data).some(value => value.toLowerCase().includes(searchTerm))))
		);
	}

	private matchSeries(entityKey: string): boolean {
		if (!this.searchRequest.series || this.searchRequest.series.size === 0) return true;
		return this.searchRequest.series!.has(seriesKey(entityKey));
	}

	private matchEpisodes(entityKey: string): boolean {
		if (!this.searchRequest.episodes || this.searchRequest.episodes.size === 0) return true;
		return this.searchRequest.episodes!.has(entityKey);
	}

	private matchTags(item: Entity): boolean {
		if (!this.searchRequest.tags || this.searchRequest.tags.size === 0) return true;
		return Array.from(this.searchRequest.tags).every(tag =>
			Array.from(item.tags.values()).some(t => t.includes(tag))
		);
	}
}